import React, { useRef, useEffect } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTouchHandler } from '../hooks/useTouchHandler';
import Portal from './Portal';

export default function Selector({
  title,
  icon,
  items,
  selected,           // string | string[] (multi)
  onSelect,           // (id) for single; (id, checked) for multi
  multi = false,
  onEdit,             // (item) optional
  showNone = true,
  disabled = false,
  // New props for modifiers
  type = null,        // 'modifier' for special handling
  modifierLevels = {}, // For modifiers: current levels
  onAdjust = null,    // For modifiers: (id, delta) callback
  getModifierLabels = null, // For modifiers: get left/right labels
  getLevelDisplayName = null, // For modifiers: get level display name
}) {
  const { handleTouchStart, handleTouchMove, handleTouchEnd } =
  useTouchHandler(() => setOpen(false));
  const [open, setOpen] = React.useState(false);
  const [dropdownStyle, setDropdownStyle] = React.useState({});
  const [dropdownDirection, setDropdownDirection] = React.useState('down'); // 'up' or 'down'
  const buttonRef = useRef(null);

  // Update dropdown position when button position changes
  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // Mobile: full-width bottom sheet
        setDropdownStyle({
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          transform: 'none'
        });
      } else {
        // Desktop: check available space and position accordingly
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 300; // Approximate dropdown height
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // If not enough space below, position above the button
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          setDropdownStyle({
            position: 'fixed',
            bottom: viewportHeight - rect.top + 4, // Position above button
            left: rect.left,
            minWidth: rect.width,
            transform: 'none'
          });
          setDropdownDirection('up');
        } else {
          // Position below button (default)
          setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            minWidth: rect.width,
            transform: 'none'
          });
          setDropdownDirection('down');
        }
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  const isSelected = (id) => multi ? (selected || []).includes(id) : selected === id;

  const handleClick = (id) => {
    if (multi) {
      const checked = !isSelected(id);
      onSelect(id, checked);
    } else {
      onSelect(id);
      setOpen(false);
    }
  };

  // Helper function for modifiers
  const getModifierLevel = (modifierId) => {
    return parseInt(modifierLevels[modifierId] || 0);
  };

  return (
    <div
      className="widget-container"
      onMouseEnter={(e) => { const img = e.currentTarget.querySelector('.widget-icon'); if (img) img.src = icon?.gif || icon?.png; }}
      onMouseLeave={(e) => { const img = e.currentTarget.querySelector('.widget-icon'); if (img) img.src = icon?.png; }}
    >
      <button
        ref={buttonRef}
        onClick={() => { if (!disabled) setOpen(!open); }}
        className="widget-button"
        data-type={title.toLowerCase()}
        disabled={disabled}
        aria-disabled={disabled}
        title={disabled ? `${title} (loading…)` : title}
      >
        <img src={icon?.png} alt={title} className="widget-icon" />
        {disabled ? `${title} (loading…)` : title}
        <ChevronDown className={`chevron-icon ${open ? (dropdownDirection === 'up' ? 'rotated' : '') : ''}`} />
      </button>

      <Portal isOpen={open}>
        <>
          <div className="dropdown-backdrop show" onClick={() => setOpen(false)} />
          <div 
            className={`dropdown-menu show ${type === 'modifier' ? 'modifiers-dropdown' : ''}`}
            onClick={(e) => e.stopPropagation()}
            style={dropdownStyle}
          >
            <div className="modal-gripper"
                 onTouchStart={handleTouchStart}
                 onTouchMove={handleTouchMove}
                 onTouchEnd={handleTouchEnd}>
              <div className="gripper-line"></div>
            </div>
            {items.length === 0 ? (
              <div className="empty-state">
                <p>No {title.toLowerCase()}s created yet</p>
              </div>
            ) : (
              <>
                {showNone && !multi && type !== 'modifier' && (
                  <button onClick={() => handleClick('')} className={`dropdown-item ${!selected ? 'selected' : ''}`}>
                    <div className="dropdown-title">None</div>
                  </button>
                )}

                {items.map(item => (
                  <div key={item.id} className="dropdown-item-with-menu">
                    {type === 'modifier' ? (
                      // Special rendering for modifiers
                      <div className="modifier-item-compact">
                        <div className="modifier-row">
                          <span className="modifier-name">{item.name}</span>
                          <div className="modifier-controls-compact">
                            <span className="modifier-label left">{getModifierLabels?.(item)?.left || ''}</span>
                            <button 
                              onClick={() => onAdjust?.(item.id, -1)} 
                              className={`modifier-btn-compact ${getModifierLevel(item.id) !== 0 ? 'active' : ''}`} 
                              disabled={getModifierLevel(item.id) <= -3}
                            >
                              <ChevronLeft className="chevron-icon" />
                            </button>
                            <div className="modifier-level-compact">
                              {getModifierLevel(item.id) !== 0 ? (
                                <span className={`level-name ${getModifierLevel(item.id) > 0 ? 'positive' : 'negative'}`}>
                                  {getLevelDisplayName?.(item, getModifierLevel(item.id)) || getModifierLevel(item.id)}
                                </span>
                              ) : <span className="level-name neutral">Neutral</span>}
                            </div>
                            <button 
                              onClick={() => onAdjust?.(item.id, 1)} 
                              className={`modifier-btn-compact ${getModifierLevel(item.id) !== 0 ? 'active' : ''}`} 
                              disabled={getModifierLevel(item.id) >= 3}
                            >
                              <ChevronRight className="chevron-icon" />
                            </button>
                            <span className="modifier-label right">{getModifierLabels?.(item)?.right || ''}</span>
                          </div>
                          {onEdit && (
                            <button onClick={() => onEdit({ ...item, type: 'modifier' })} className="simple-kebab-btn" title="Edit modifier">⋮</button>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Standard rendering for other types
                      <>
                        <button
                          onClick={() => handleClick(item.id)}
                          className={`dropdown-item ${isSelected(item.id) ? 'selected' : ''}`}
                        >
                          <div className="dropdown-content">
                            <div className="dropdown-title">{item.name}</div>
                            <div className="dropdown-description">{item.description}</div>
                          </div>
                        </button>
                        {onEdit && (
                          <button onClick={() => onEdit(item)} className="simple-kebab-btn" title={`Edit ${title.toLowerCase()}`}>
                            ⋮
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      </Portal>
    </div>
  );
}
